<template>
	<section class="vip-home-card" :aria-label="$t('personalCenter.vip_member')" :aria-busy="isLoading ? 'true' : 'false'">
		<div class="vip-home-main">
			<div v-if="memberLevel === 0" class="vip-home-emblem" aria-hidden="true">VIP0</div>
			<div v-else class="vip-home-emblem-photo">
				<img class="vip-home-emblem-image" :src="emblemImage" :alt="'VIP' + memberLevel"
					@error="useFallbackEmblemImage = true">
			</div>
			<div class="vip-home-copy">
				<div class="vip-home-heading">
					<span>{{ $t('member_level.title') }}</span>
					<strong class="vip-home-level-name" :title="memberLevelName">
						<template v-if="levelNameParts.isVip">
							<span class="vip-home-level-mark" aria-hidden="true"><span>VIP</span></span>
							<span class="vip-home-level-number">{{ levelNameParts.suffix }}</span>
						</template>
						<span v-else class="vip-home-level-text">{{ memberLevelName }}</span>
					</strong>
				</div>
				<p class="vip-home-points">
					<span>{{ $t('member_level.points_progress') }}</span>
					<strong>{{ formatNumber(currentPoints) }}<template v-if="!isMaxLevel"> / {{ formatNumber(nextLevelPoints) }}</template></strong>
				</p>
				<div class="vip-home-progress" role="progressbar" aria-valuemin="0"
					:aria-valuemax="isMaxLevel ? 100 : nextLevelPoints"
					:aria-valuenow="isMaxLevel ? 100 : currentPoints">
					<span :style="{ width: progressDisplayPercent + '%' }"></span>
				</div>
				<p v-if="isMaxLevel" class="vip-home-next vip-home-max-level">
					<i class="el-icon-trophy" aria-hidden="true"></i>
					{{ $t('personalCenter.vip.max_level') }}
				</p>
				<p v-else class="vip-home-next">
					{{ $t('member_level.next_level', { level: nextLevel }) }}
					<strong>{{ formatNumber(remainingPoints) }}</strong>
					{{ $t('member_level.points_unit') }}
				</p>
			</div>
		</div>
		<button class="vip-home-action" type="button" @click="viewRewards">
			<span>{{ $t('member_level.view_rewards') }}</span>
			<i class="el-icon-arrow-right" aria-hidden="true"></i>
		</button>
	</section>
</template>

<script>
export default {
	name: 'VipMemberHomeCard',
	props: {
		gameData: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			memberData: null,
			isLoading: false,
			useFallbackEmblemImage: false
		}
	},
	computed: {
		memberLevel() {
			return Math.max(0, Number(this.memberData && this.memberData.currentVipLevel) || 0)
		},
		isMaxLevel() {
			return this.memberData !== null && this.memberData.nextLevel === null
		},
		memberLevelName() {
			return (this.memberData && this.memberData.levelName) || `VIP${this.memberLevel}`
		},
		levelNameParts() {
			const name = String(this.memberLevelName || '').trim()
			const matched = /^VIP\s*(.*)$/i.exec(name)
			return matched
				? { isVip: true, suffix: matched[1] || String(this.memberLevel) }
				: { isVip: false, suffix: '' }
		},
		levelImage() {
			const level = Math.min(15, Math.floor(this.memberLevel))
			return `/static/image/vip/vip${level}.png`
		},
		emblemImage() {
			const roleInfo = this.$cache.getRoleInfo() || {}
			const fallbackImage = roleInfo.avatar || roleInfo.avatarUrl || roleInfo.headImage ||
				this.gameData.gameIcon || this.levelImage
			if (this.useFallbackEmblemImage) return fallbackImage
			return (this.memberData && this.memberData.iconUrl) || fallbackImage
		},
		nextLevelData() {
			return this.memberData && this.memberData.nextLevel ? this.memberData.nextLevel : {}
		},
		nextLevel() {
			return Math.max(this.memberLevel,
				Number(this.nextLevelData.levelNo) || this.memberLevel + 1)
		},
		currentPoints() {
			return Math.max(0, Number(this.memberData && this.memberData.totalExp) || 0)
		},
		nextLevelPoints() {
			return Math.max(1, Number(this.nextLevelData.minExp) || 1)
		},
		remainingPoints() {
			return Math.max(0, this.nextLevelPoints - this.currentPoints)
		},
		progressPercent() {
			if (this.isMaxLevel) return 100
			return Math.min(100, Math.max(0, this.currentPoints / this.nextLevelPoints * 100))
		},
		progressDisplayPercent() {
			return this.progressPercent > 0 ? Math.max(2, this.progressPercent) : 0
		}
	},
	mounted() {
		this.loadMemberData()
	},
	methods: {
		async loadMemberData() {
			const userId = this.$session.getUserId()
			const gameCode = this.gameData.gameCode || this.$cache.getGameCode()
			if (!userId || !gameCode) return

			this.isLoading = true
			try {
				const response = await this.$axios({
					method: 'get',
					url: '/web/api/member/initData',
					silentError: true,
					params: { userId, gameCode }
				})
				if (response && response.code === 1000 && response.data) {
					this.useFallbackEmblemImage = false
					this.memberData = response.data
				}
			} catch (error) {
				this.$emit('load-error', error)
			} finally {
				this.isLoading = false
			}
		},
		formatNumber(value) {
			return new Intl.NumberFormat(String(this.$i18n.locale || 'en_US').replace('_', '-')).format(value)
		},
		viewRewards() {
			this.$router.push({ name: 'profile', query: { tab: 'vip' } })
		}
	}
}
</script>

<style scoped>
.vip-home-card {
	box-sizing: border-box;
	width: 100%;
	margin-top: 12px;
	border: 1px solid #e5e7ea;
	border-radius: 8px;
	padding: 14px;
	background: #fff;
	box-shadow: 0 5px 16px rgba(31, 38, 47, .07);
}

.vip-home-main {
	display: grid;
	grid-template-columns: 58px minmax(0, 1fr);
	align-items: center;
	gap: 13px;
}

.vip-home-emblem {
	display: grid;
	width: 56px;
	height: 56px;
	place-items: center;
	box-sizing: border-box;
	border: 3px solid #fff1bd;
	border-radius: 50%;
	color: #8b5310;
	background: #ffd96f;
	box-shadow: 0 0 0 1px #e7af38, 0 4px 10px rgba(91, 58, 18, .14);
	font-size: 13px;
	font-weight: 800;
}

.vip-home-emblem-photo {
	width: 56px;
	height: 56px;
	box-sizing: border-box;
	overflow: hidden;
	border: 2px solid rgba(var(--theme-color-rgb, 210, 106, 44), .78);
	border-radius: 50%;
	padding: 3px;
	background: #fff;
	box-shadow: 0 4px 10px rgba(91, 58, 18, .14);
}

.vip-home-emblem-image {
	display: block;
	width: 100%;
	height: 100%;
	border-radius: 50%;
	object-fit: cover;
}

.vip-home-copy { min-width: 0; }

.vip-home-heading {
	display: flex;
	align-items: center;
	gap: 8px;
	color: #7b5a32;
	font-size: 11px;
	line-height: 18px;
}

.vip-home-level-name {
	display: inline-flex;
	min-width: 0;
	max-width: 126px;
	align-items: center;
	gap: 4px;
	overflow: hidden;
	white-space: nowrap;
}

.vip-home-level-mark {
	position: relative;
	display: grid;
	width: 48px;
	height: 29px;
	flex: 0 0 48px;
	place-items: center;
	filter: drop-shadow(0 2px 2px rgba(121, 54, 12, .2));
}

.vip-home-level-mark::before,
.vip-home-level-mark::after {
	position: absolute;
	content: '';
	clip-path: polygon(50% 0, 62% 13%, 78% 7%, 82% 25%, 100% 31%, 87% 49%, 92% 68%, 68% 70%, 50% 100%, 32% 70%, 8% 68%, 13% 49%, 0 31%, 18% 25%, 22% 7%, 38% 13%);
}

.vip-home-level-mark::before {
	inset: 0;
	background: #f4b62f;
}

.vip-home-level-mark::after {
	inset: 3px 4px 4px;
	background: #e6461f;
}

.vip-home-level-mark span {
	position: relative;
	z-index: 1;
	color: #fff5c6;
	font-family: Georgia, 'Times New Roman', serif;
	font-size: 18px;
	font-style: italic;
	font-weight: 900;
	line-height: 1;
	text-shadow: 0 1px 0 #8e2712, 0 0 2px rgba(255, 241, 164, .8);
}

.vip-home-level-number {
	position: relative;
	top: -2px;
	color: #df4a21;
	background: linear-gradient(180deg, #ffb54e 0%, #f9782c 45%, #df3d1d 100%);
	-webkit-background-clip: text;
	background-clip: text;
	-webkit-text-fill-color: transparent;
	font-family: Georgia, 'Times New Roman', serif;
	font-size: 26px;
	font-weight: 900;
	line-height: 29px;
}

.vip-home-level-text {
	display: inline-block;
	max-width: 100%;
	overflow: hidden;
	color: var(--theme-color, #d26a2c);
	background: linear-gradient(180deg, #f7ad4a 0%, #e96927 55%, #c83c1b 100%);
	-webkit-background-clip: text;
	background-clip: text;
	-webkit-text-fill-color: transparent;
	padding: 0 2px 2px;
	font-size: 17px;
	font-weight: 900;
	line-height: 24px;
	text-overflow: ellipsis;
}

.vip-home-points {
	display: flex;
	min-width: 0;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	margin: 5px 0 0;
	color: #666d76;
	font-size: 11px;
	line-height: 17px;
}

.vip-home-points strong { color: #34383e; font-size: 12px; white-space: nowrap; }

.vip-home-progress {
	height: 7px;
	margin-top: 7px;
	overflow: hidden;
	border-radius: 4px;
	background: #e5e7e9;
}

.vip-home-progress span {
	display: block;
	height: 100%;
	border-radius: inherit;
	background: var(--theme-color, #d26a2c);
}

.vip-home-next {
	margin: 7px 0 0;
	color: #656c75;
	font-size: 11px;
	line-height: 17px;
}

.vip-home-next strong { color: var(--theme-color, #d26a2c); font-weight: 800; }

.vip-home-max-level {
	display: flex;
	align-items: center;
	gap: 5px;
	color: #9b621d;
	font-weight: 800;
}

.vip-home-max-level i { color: #dda037; font-size: 14px; }

.vip-home-action {
	display: flex;
	width: 100%;
	height: 38px;
	align-items: center;
	justify-content: center;
	gap: 8px;
	margin-top: 13px;
	border: 1px solid rgba(var(--theme-color-rgb, 210, 106, 44), .34);
	border-radius: 6px;
	color: var(--theme-color, #d26a2c);
	background: rgba(var(--theme-color-rgb, 210, 106, 44), .075);
	font-size: 13px;
	font-weight: 800;
	cursor: pointer;
}

.vip-home-action:hover { background: rgba(var(--theme-color-rgb, 210, 106, 44), .12); }
.vip-home-action:focus-visible { outline: 2px solid rgba(var(--theme-color-rgb, 210, 106, 44), .45); outline-offset: 2px; }

@media (max-width: 767px) {
	.vip-home-card { margin-top: 10px; padding: 12px; }
	.vip-home-main { grid-template-columns: 52px minmax(0, 1fr); gap: 11px; }
	.vip-home-emblem { width: 50px; height: 50px; font-size: 12px; }
	.vip-home-emblem-photo { width: 50px; height: 50px; }
	.vip-home-level-name { max-width: 110px; }
	.vip-home-level-mark { width: 44px; height: 27px; flex-basis: 44px; }
	.vip-home-level-mark span { font-size: 16px; }
	.vip-home-level-number { font-size: 21px; line-height: 27px; }
	.vip-home-level-text { font-size: 16px; line-height: 22px; }
}
</style>
